import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Zap, Lightbulb, Megaphone, PartyPopper, AlertTriangle } from "lucide-react";
import type { Announcement } from "@/hooks/useAnnouncements";

interface AnnouncementCardProps {
  announcement: Announcement;
  isFeatured?: boolean;
}

const typeConfig = {
  welcome: {
    icon: PartyPopper,
    label: "Boas-vindas",
    variant: "default" as const,
  },
  feature: {
    icon: Zap,
    label: "Funcionalidade",
    variant: "secondary" as const,
  },
  tip: {
    icon: Lightbulb,
    label: "Dica",
    variant: "outline" as const,
  },
  update: {
    icon: Megaphone,
    label: "Novidade",
    variant: "default" as const,
  },
  highlight: {
    icon: AlertTriangle,
    label: "Destaque",
    variant: "default" as const,
  },
};

const parseMarkdownBold = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return <strong key={index} className="font-semibold">{part}</strong>;
    }
    return part;
  });
};

const renderContent = (content: string) => {
  const lines = content.split('\n');
  return lines.map((line, index) => {
    if (line.trim() === '') {
      return <br key={index} />;
    }
    return (
      <p key={index} className="mb-1">
        {parseMarkdownBold(line)}
      </p>
    );
  });
};

const extractYouTubeId = (url: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

export const AnnouncementCard = ({ announcement, isFeatured }: AnnouncementCardProps) => {
  const config = typeConfig[announcement.type] || typeConfig.update;
  const Icon = config.icon;
  const isHighlight = announcement.type === 'highlight';

  return (
    <Card className={`${isHighlight ? 'border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-red-50' : isFeatured ? 'border-primary bg-primary/5' : ''} transition-all hover:shadow-md`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {isHighlight ? (
              <div className="p-2 rounded-full bg-orange-100">
                <Icon className="h-5 w-5 text-orange-600" />
              </div>
            ) : (
              <Icon className={`h-5 w-5 ${isFeatured ? 'text-primary' : 'text-muted-foreground'}`} />
            )}
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">{announcement.title}</CardTitle>
              {isHighlight && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-orange-500 text-white rounded-full">NOVO</span>
              )}
            </div>
          </div>
          {!isHighlight && <Badge variant={config.variant}>{config.label}</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground space-y-1">
          {renderContent(announcement.content)}
        </div>

        {announcement.button_text && announcement.button_link && (
          <div className="mt-4">
            <Button asChild variant="outline" className={isHighlight ? "border-orange-300 hover:bg-orange-100" : ""}>
              <Link to={announcement.button_link}>
                {isHighlight && <AlertTriangle className="h-4 w-4 mr-2" />}
                {announcement.button_text}
              </Link>
            </Button>
          </div>
        )}

        {announcement.video_url && (
          <div className="mt-4 aspect-video rounded-lg overflow-hidden">
            {extractYouTubeId(announcement.video_url) ? (
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${extractYouTubeId(announcement.video_url)}`}
                title={announcement.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={announcement.video_url}
                controls
                className="w-full h-full object-cover"
              />
            )}
          </div>
        )}

        {announcement.image_url && !announcement.video_url && (
          <div className="mt-4">
            <img
              src={announcement.image_url}
              alt={announcement.title}
              className="rounded-lg w-full max-h-64 object-cover"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
